import { Button, CopyCTA, Form, Grid, Tooltip as LCTooltip } from '@livecontrol/core-ui';
import { Event } from '@livecontrol/scheduler/model';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import styled from 'styled-components';
import { DestinationsList } from '../../../components';
import { useTimeChangeVariable } from '../../../hooks';
import { MasqueradeToken } from '../../../model';
import { Store, store } from '../../../store';
import type { ScheduledEvent } from '../graphql';
import { useCheckinEvent, useCreateTicket, useUpdateEventAdminFields } from '../graphql';
import { UploadVideo } from './upload-file';
import Phase = Event.Phase;

const SummaryHeader = styled.div`
  border-bottom: 1px solid var(--white40);
  padding-bottom: 12px;
  margin-bottom: 24px;

  .title {
    font-size: 18px;
    line-height: 22px;
  }

  .subtitle {
    font-size: 14px;
    line-height: 18px;
  }

  .pill-button {
    background-color: var(--white20);
    border: none;
    box-shadow: none;
    width: 100%;
  }
`;

const HighPriorityContainer = styled.div`
  padding: 10px 0px;

  .high-priority-checkbox{
    width: 24px;
    margin-right: 16px;
    cursor:pointer;
  }

  .high-priority-checkbox:disabled{
    background-color: var(--white10);
  }
  
  .high-priority-textarea:disabled{
    cursor: not-allowed;
    background-color: var(--white10);
    color: #495057;
  }

  .small-text{
    font-size:14px;
  }
`;

// @fixme: Josh- make this use the distributions field on scheduledEvent
export const EventDetailsSummary = ({
  scheduledEvent,
  togglePlayer,
  handleRefresh
}: {
  scheduledEvent: ScheduledEvent;
  togglePlayer: () => void;
  handleRefresh: () => Promise<void>;
}): React.ReactElement => {
  const { token, me } = Store.User.useIdentity();
  const { environment } = store.getState();
  const currentTime = useRef(DateTime.now()).current;
  const [mutation, { success, loading }] = useCreateTicket();
  const [isSuccess] = useTimeChangeVariable(success, 2000);
  const [checkinEvent] = useCheckinEvent();

  const [highPriority, setHighPriority] = useState(scheduledEvent.high_priority);
  const [highPriorityNote, setHighPriorityNote] = useState(scheduledEvent.high_priority_note);
  const [saved, setSaved] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);


  const [updateEvent, { loading: loadingAdminFields, error: errorAdminFields }] = useUpdateEventAdminFields();

  const showSavedAdminFieldsStatus = useCallback(() => {
    setSaved(true);

    setTimeout(() => {
      setSaved(false);
    }, 2000); // Hide after seconds
  }, []);

  const updateEventField = useCallback(async (updates: {
    highPriority?: boolean;
    highPriorityNote?: string;
  }) => {
    try {

      await updateEvent({
        id: Number(scheduledEvent.id),
        ...updates
      });

      showSavedAdminFieldsStatus();
      void handleRefresh();
    } catch (error_: unknown) {
      console.error('Error updating event:', error_);
    }
  }, [scheduledEvent.id, updateEvent, showSavedAdminFieldsStatus]);

  const handleCheckboxChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;

    setHighPriority(newValue);

    // Cancelar cualquier debounce pendiente del textarea
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }

    await updateEventField({ highPriority: newValue });
  }, [debounceTimer, updateEventField]);

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;

    setHighPriorityNote(newValue);

    // Limpiar el timer anterior
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Crear nuevo timer para debounce (2 segundos)
    const newTimer = setTimeout(async () => {
      await updateEventField({ highPriorityNote: newValue });
    }, 2000);

    setDebounceTimer(newTimer);
  }, [debounceTimer, updateEventField]);

  useEffect(() => (): void => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    }, [debounceTimer]);

  const createTicket = useCallback(async () => {
    await mutation(scheduledEvent.id);
  }, [mutation, scheduledEvent.id]);

  const toggleCheckEvent = useCallback(() => {
    void checkinEvent({
      eventId: scheduledEvent.id,
      locationId: scheduledEvent.location?.id,
      checkedUser: !scheduledEvent.checked_user && me?.id ? Number(me.id) : undefined
    });

  }, [checkinEvent, me?.id, scheduledEvent.checked_user, scheduledEvent.id, scheduledEvent.location?.id]);

  const checkedUser = useMemo(() => {
    if (!scheduledEvent.checked_user) {
      return undefined;
    }

    const { first_name, last_name } = scheduledEvent.checked_user;

    return `${first_name ?? ''} ${last_name ?? ''}`;
  }, [scheduledEvent.checked_user]);

  const isEditable = useMemo(
    () => DateTime.fromISO(scheduledEvent.start_time) > currentTime,
    [currentTime, scheduledEvent]
  );

  const masqueradeUrl = useMemo((): string | undefined => {
    if (token) {
      const decoded = MasqueradeToken.decode(token.jwt);

      return decoded
        ? `${environment.DASHBOARD_URI}/masquerade#token=${decoded.jwt}&xref=${scheduledEvent.location?.user?.id ?? ''}:${
            scheduledEvent.location?.user?.id ?? ''
          }&redirect=%2Fschedule%2Fedit%2F${scheduledEvent.id}`
        : undefined;
    }

    return undefined;
  }, [environment, token, scheduledEvent]);

  const startTime = DateTime.fromISO(scheduledEvent.start_time);

  const endTime = DateTime.fromISO(scheduledEvent.end_time);

  const { hours, minutes } = useMemo(() => {
    const duration = endTime.diff(startTime).shiftTo('hours', 'minutes').toObject();
    const { hours: hours_ = 0, minutes: minutes_ = 0 } = duration;

    return {
      hours: Math.round(minutes_) === 60 ? hours_ + 1 : hours_,
      minutes: Math.round(minutes_) % 60
    };
  }, [endTime, startTime]);

  const operator = `${scheduledEvent.assignedOperator?.at(0) ?? 'Unknown'} ${
    scheduledEvent.operatorType ? `(${scheduledEvent.operatorType})` : ''
  }`;

  const muxDashboardUrl = scheduledEvent.assets?.mux_asset_id ? `https://dashboard.mux.com/environments/599vti/video/assets/${scheduledEvent.assets.mux_asset_id}` : null;

  const hasAllChecked = useMemo(() => scheduledEvent.operatorInfo ? Object.values(scheduledEvent.operatorInfo).every(value => value) : false, [scheduledEvent.operatorInfo]);

  return (
    <div>
      <SummaryHeader className='d-flex flex-column justify-content-between gap-8px'>
        <div className="d-flex justify-content-between">
          <Grid gridTemplateColumns='1fr' gridTemplateRows='min-content' gap='8px'>
            <OverlayTrigger
              placement='top'
              overlay={
                <Tooltip id={_.uniqueId('tooltip')}>{scheduledEvent.location?.user?.organization_name}</Tooltip>
              }
            >
              <a
                className='text-white title text-truncate text-nowrap'
                href={scheduledEvent.webplayer_url ?? undefined}
                target='_blank'
                rel='noreferrer'
              >
                {scheduledEvent.location?.user?.organization_name ?? ''} <i className='far fa-external-link ml-4px' />
              </a>
            </OverlayTrigger>
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip id={_.uniqueId('tooltip')}>{scheduledEvent.name}</Tooltip>}
            >
              <span className='text-white subtitle text-truncate text-nowrap'>
                {scheduledEvent.name}
              </span>
            </OverlayTrigger>

            {scheduledEvent.event_type === 'Produced' && (
              <OverlayTrigger
                placement='bottom'
                overlay={<Tooltip id={_.uniqueId('tooltip')}>Operator: {operator}</Tooltip>}
              >
                <span className='text-white subtitle text-truncate text-nowrap'>
                  Operator: {operator}
                </span>
              </OverlayTrigger>
            )}
          </Grid>
          <div>
            <Grid
              gridTemplateColumns='repeat(3, 1fr)'
              gap='8px'
              className='mb-12px'
              style={{ direction: 'rtl' }}
            >
              {muxDashboardUrl && (
                <Button
                  size='sm'
                  className='pill-button'
                  style={{ direction: 'ltr' }}
                  href={muxDashboardUrl}
                  target='_blank'
                >
                  MUX URL <i className='far fa-external-link ml-4px' />
                </Button>
              )}
              <CopyCTA
                noIcon
                toastText='CID Copied'
                value={`CID:${scheduledEvent.location?.user?.id ?? ''}`}
              >
                <Button size='sm' className='pill-button'>
                  CID: {scheduledEvent.location?.user?.id ? `${scheduledEvent.location.user.id}.${scheduledEvent.location.secondary_id!}` : ''}
                </Button>
              </CopyCTA>

              <CopyCTA noIcon toastText='EID Copied' value={`EID:${scheduledEvent.id.toString()}`}>
                <Button size='sm' className='pill-button'>
                  EID: {scheduledEvent.id}
                </Button>
              </CopyCTA>
              {scheduledEvent.location?.splashtop_uuid && (
                <Button
                  size='sm'
                  className='pill-button'
                  style={{ direction: 'ltr' }}
                  href={`st-business://com.splashtop.business?account=${me?.splashtop_email ?? me?.email ?? ''}&uuid=${scheduledEvent.location.splashtop_uuid}&sessiontype=remote`}
                  target='_blank'
                >
                  SPLASHTOP <i className='far fa-external-link ml-4px' />
                </Button>
              )}

            </Grid>
          </div>
        </div>
        <Grid gridTemplateColumns='repeat(3, 1fr)' gap='8px' style={{ direction: 'rtl' }}>
            {scheduledEvent.status === Phase.Past.toString() && scheduledEvent.assets?.id && scheduledEvent.location?.user?.id && (
              <UploadVideo
              assetId={scheduledEvent.assets.id}
              clientId={scheduledEvent.location.user.id}
              togglePlayer={togglePlayer}
              />
            )}
            {isEditable && masqueradeUrl && (
              <Button
                className="align-content-center"
                href={masqueradeUrl}
                target='_blank'
                size='sm'
                variant='outline-light'
                style={{ direction: 'ltr' }}
              >
                <i className='far fa-edit mr-8px' />
                Edit Event
              </Button>
            )}
            <LCTooltip tip={
              <div className="d-flex flex-column align-items-start">
                <span>
                  {scheduledEvent.operatorInfo?.checklist_correct_encoder ? (
                    <i className="fas fa-check text-success mr-8px" />
                  ) : (
                    <i className="fas fa-times mr-8px" style={{ color: '#ff505f' }} />
                  )}
                  Encoder verification
                </span>
                <span>
                  {scheduledEvent.operatorInfo?.checklist_presets ? (
                    <i className="fas fa-check text-success mr-8px" />
                  ) : (
                    <i className="fas fa-times mr-8px" style={{ color: '#ff505f' }} />
                  )}
                  Presets
                </span>
                <span>
                  {scheduledEvent.operatorInfo?.checklist_audio ? (
                    <i className="fas fa-check text-success mr-8px" />
                  ) : (
                    <i className="fas fa-times mr-8px" style={{ color: '#ff505f' }} />
                  )}
                  Audio
                </span>
                <span>
                  {scheduledEvent.operatorInfo?.checklist_obs ? (
                    <i className="fas fa-check text-success mr-8px" />
                  ) : (
                    <i className="fas fa-times mr-8px" style={{ color: '#ff505f' }} />
                  )}
                  OBS output
                </span>
                <span>
                  {scheduledEvent.operatorInfo?.checklist_notes ? (
                    <i className="fas fa-check text-success mr-8px" />
                  ) : (
                    <i className="fas fa-times mr-8px" style={{ color: '#ff505f' }} />
                  )}
                  Event notes
                </span>
              </div>
              }
            >
              <Button
              variant='warning'
              disabled
              className='opacity-90'
              style={{ direction: 'ltr' }}
              >
                {hasAllChecked ? (
                  <i className="fas fa-check-circle text-success mr-8px" />
                ) : (
                <i className="fas fa-times mr-8px" style={{ color: '#ff505f' }} />
                )}
                Pre-Check
              </Button>
            </LCTooltip>
            <Button
            variant='outline-light'
            size='sm'
            style={{ direction: 'ltr' }}
            disabled={loading}
            onClick={createTicket}
            >
              {isSuccess === true ? (
                <>
                <i className="fas fa-check-circle text-success mr-8px" />
                success
                </>
              ) : isSuccess === false ? (
                <>
                <i className="fas fa-times mr-8px" style={{ color: '#ff505f' }} />
                failed
                </>
              ) : (
                <>
                <i className="mr-8px">
                  <svg fill="currentColor" width="100%" height="100%" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg">
                    <g strokeWidth="0"/>
                    <g strokeLinecap="round" strokeLinejoin="round"/>
                    <g>
                      <title>zendesk</title>
                      <path d="M24.068 20.44c-0.001 0-0.002 0-0.003 0-3.823 0-6.923 3.1-6.923 6.923 0 0.001 0 0.003 0 0.004v-0h13.854c0-0 0-0 0-0 0-3.825-3.101-6.926-6.926-6.926-0 0-0 0-0 0h0zM14.858 10.643l-13.854 16.724h13.854zM1.004 4.636c0 3.826 3.101 6.927 6.927 6.927s6.927-3.101 6.927-6.927v0zM17.142 4.633v16.728l13.854-16.726h-13.854z"/>
                    </g>
                  </svg>
                </i>
                Create Ticket
                </>
              )}
            </Button>
            <LCTooltip
              tip={checkedUser ? (`Checked-in by: ${checkedUser}`) : null}
            >
              <Button
              variant='outline-light'
              onClick={toggleCheckEvent}
              >
                <span>
                  <i className={`${checkedUser ? 'fa' : 'far'} fa-thumbs-up`} style={{ width: 20, height: 20 }} />
                </span>
              </Button>
            </LCTooltip>
          </Grid>
      </SummaryHeader>
      <Grid
        gridTemplateColumns='repeat(auto-fill, minmax(100px, min(100%/3, max(120px, 100%/5))))'
        rowGap='24px'
      >
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Event Type'
            description={
              scheduledEvent.event_type === 'Produced' ? (
                <span className='d-flex align-items-center'>
                  <i className='far fa-camera-movie mr-8px' />
                  <span className='text-truncate text-nowrap'>Produced</span>
                </span>
              ) : scheduledEvent.event_type === 'SimLive' ? (
                <span className='d-flex align-items-center'>
                  <i className='far fa-calendar-day mr-8px' />
                  <span className='text-truncate text-nowrap'>Simulated Live</span>
                </span>
              ) : scheduledEvent.event_type === 'Mobile' ? (
                <span className='d-flex align-items-center'>
                  <i className='far fa-mobile mr-8px' />
                  <span className='text-truncate text-nowrap'>Mobile</span>
                </span>
              ) : scheduledEvent.event_type === 'Mobile Kit' ? (
                <span className='d-flex align-items-center'>
                  <i className='far fa-mobile mr-8px' />
                  <span className='text-truncate text-nowrap'>Mobile Kit</span>
                </span>
              ) : scheduledEvent.event_type === 'Test' ? (
                <span className='d-flex align-items-center'>
                  <i className='far fa-broadcast-tower mr-8px' />
                  <span className='text-truncate text-nowrap'>Test</span>
                </span>
              ) : (
                <span className='d-flex align-items-center'>
                  <i className='far fa-camera-home mr-8px' />
                  <span className='text-truncate text-nowrap'>Static</span>
                </span>
              )
            }
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Start Time'
            description={
              <OverlayTrigger
                placement='bottom'
                overlay={
                  <Tooltip id={_.uniqueId('tooltip')}>
                    Client Time:{' '}
                    {startTime.setZone(scheduledEvent.location?.user?.time_zone ?? '').toFormat('t ZZZZ')}
                  </Tooltip>
                }
              >
                <span>{startTime.toFormat('t ZZZZ')}</span>
              </OverlayTrigger>
            }
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='End Time'
            description={
              <OverlayTrigger
                placement='bottom'
                overlay={
                  <Tooltip id={_.uniqueId('tooltip')}>
                    Client Time:{' '}
                    {endTime.setZone(scheduledEvent.location?.user?.time_zone ?? '').toFormat('t ZZZZ')}
                  </Tooltip>
                }
              >
                <span>{endTime.toFormat('t ZZZZ')}</span>
              </OverlayTrigger>
            }
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Length'
            description={`${hours && hours > 0 ? `${hours} hr ` : ''} ${
              minutes && minutes > 0 ? `${minutes} min` : ''
            }`}
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Date'
            description={
              <OverlayTrigger
                placement='bottom'
                overlay={
                  <Tooltip id={_.uniqueId('tooltip')}>
                    Client Local Time:{' '}
                    {startTime
                      .setZone(scheduledEvent.location?.user?.time_zone ?? '')
                      .toLocaleString(DateTime.DATE_SHORT)}
                  </Tooltip>
                }
              >
                <span>{startTime.toLocaleString(DateTime.DATE_SHORT)}</span>
              </OverlayTrigger>
            }
          />
        </Grid.Item>
        <Grid.Item>
          {scheduledEvent.location?.id && (
            <CopyCTA noIcon toastText='Location ID Copied' value={scheduledEvent.location.id}>
              <Form.Descriptor
                titleClassName='text-white60 font-weight-light'
                descriptionClassName='text-white mt-8px'
                title='Location'
                description={
                  <OverlayTrigger
                    placement='bottom'
                    overlay={
                      <Tooltip id={_.uniqueId('tooltip')}>
                        <div>{scheduledEvent.location.name}</div>
                        <div>{scheduledEvent.location.id}</div>
                      </Tooltip>
                    }
                  >
                    <button
                      type='button'
                      className='x-pseudo text-truncate text-nowrap w-100 text-left'
                    >
                      {scheduledEvent.location.name}
                    </button>
                  </OverlayTrigger>
                }
              />
            </CopyCTA>
          )}
        </Grid.Item>
        <Grid.Item>
          {scheduledEvent.event_password ? (
            <CopyCTA noIcon toastText='Password Copied' value={scheduledEvent.event_password}>
              <Form.Descriptor
                titleClassName='text-white60 font-weight-light'
                descriptionClassName='text-white mt-8px'
                title='Password'
                description={
                  <OverlayTrigger
                    placement='bottom'
                    overlay={
                      <Tooltip id={_.uniqueId('tooltip')}>{scheduledEvent.event_password}</Tooltip>
                    }
                  >
                    <button type='button' className='x-pseudo'>
                      Yes
                    </button>
                  </OverlayTrigger>
                }
              />
            </CopyCTA>
          ) : (
            <Form.Descriptor
              titleClassName='text-white60 font-weight-light'
              descriptionClassName='text-white mt-8px'
              title='Password'
              description='No'
            />
          )}
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Chat'
            description={scheduledEvent.live_chat ? 'Enabled' : 'Disabled'}
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Event Status'
            description={scheduledEvent.hidden ? 'Private' : 'Public'}
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Destinations'
            description={<DestinationsList {...scheduledEvent} />}
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Operator'
            description={scheduledEvent.operator_name ?? 'N/A'}
          />
        </Grid.Item>
        <Grid.Item gridColumn="span 2">
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='High Priority'
            description={
              <HighPriorityContainer className="d-flex gap-3 flex-column">
                <div className="inputs d-flex align-items-center">
                  <input
                    type="checkbox"
                    className='form-control high-priority-checkbox'
                    checked={highPriority}
                    onChange={handleCheckboxChange}
                    disabled={loadingAdminFields}
                  />
                  <textarea
                    className="flex-grow-1 form-control high-priority-textarea"
                    value={highPriorityNote}
                    onChange={handleTextareaChange}
                    disabled={!highPriority || loadingAdminFields}
                  />
                </div>
                <div className="w-100 status-section small-text d-flex flex-column mt-8px">

                  <div className="errors-section text-danger mb-2px" style={{ display: errorAdminFields ? 'block' : 'none' }}>
                    {errorAdminFields}
                  </div>

                  <div className="saved-section align-items-center mb-2px" style={{ display: saved ? 'flex' : 'none' }}>
                    <span className="text-success small-text d-flex align-items-center gap-1">
                      Saved
                      <i className="fas fa-check ml-2px" />
                    </span>
                  </div>

                  <div className="loading-section align-items-center mb-2px" style={{ display: loadingAdminFields ? 'block' : 'none' }}>
                    <span className="text-muted small-text">
                      <i className="fas fa-spinner fa-spin" />
                    </span>
                  </div>

                </div>
              </HighPriorityContainer>
            }
          />
        </Grid.Item>
      </Grid>
    </div>
  );
};
